/**
 * Customer Required Overlay – shown to employees/admins when no customer is selected.
 *
 * Displays a full-page overlay prompting the user to select a customer
 * from the sidebar or navigate to the customers page to add one.
 * The overlay blocks interaction with the underlying page content.
 */

import { navigate } from '../../infrastructure/router';
import { userService } from '../../features/order/user.service';
import { customerService } from '../../features/customers/customer.service';
import { renderCustomerSelector } from './customer-selector';
import type { CustomerSelectorHandle } from './customer-selector';
import { cartService } from '../../features/cart/cart.service';
import { customerDataService } from '../../features/customers/customer-data.service';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let overlayElement: HTMLElement | null = null;
let selectorHandle: CustomerSelectorHandle | null = null;
let unsubscribeUser: (() => void) | null = null;

// ---------------------------------------------------------------------------
// SVG Icons
// ---------------------------------------------------------------------------

const customerIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;

const addCustomerIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>`;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Show the customer-required overlay on the given content container.
 * Replaces the container content entirely.
 * If already shown, does nothing.
 */
export function showCustomerRequiredOverlay(contentContainer: HTMLElement): void {
  if (overlayElement) return;

  // Clear any existing content
  contentContainer.innerHTML = '';

  overlayElement = document.createElement('div');
  overlayElement.className = 'customer-required-overlay';
  overlayElement.setAttribute('role', 'dialog');
  overlayElement.setAttribute('aria-modal', 'true');
  overlayElement.setAttribute('aria-label', 'Kundenauswahl erforderlich');

  const card = document.createElement('div');
  card.className = 'customer-required-overlay__card';

  // Icon
  const iconEl = document.createElement('div');
  iconEl.className = 'customer-required-overlay__icon';
  iconEl.innerHTML = customerIcon;
  card.appendChild(iconEl);

  // Title
  const title = document.createElement('h2');
  title.className = 'customer-required-overlay__title';
  title.textContent = 'Kunde auswählen';
  card.appendChild(title);

  // Description
  const desc = document.createElement('p');
  desc.className = 'customer-required-overlay__desc';
  desc.textContent = 'Bitte wählen Sie einen Kunden aus, bevor Sie fortfahren. Alle Aktionen werden dem ausgewählten Kunden zugeordnet.';
  card.appendChild(desc);

  // Customer selector (inline in the overlay)
  const selectorWrapper = document.createElement('div');
  selectorWrapper.className = 'customer-required-overlay__selector';

  // Ensure customers are loaded
  if (!customerService.isLoaded()) {
    customerService.loadCustomers().catch(() => {
      // Silently handle
    });
  }

  selectorHandle = renderCustomerSelector(selectorWrapper, {
    value: '',
    onChange: (customerId) => {
      if (customerId && customerId.trim()) {
        userService.setCustomerNumber(customerId);
        // Load cart and customer data for the selected customer
        cartService.loadCart(customerId).catch(() => {});
        customerDataService.loadCustomerData(customerId).catch(() => {});
        // Remove overlay and re-trigger current route to load the page
        hideCustomerRequiredOverlay();
        // Force re-render of the current route by dispatching hashchange
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      }
    },
  });

  card.appendChild(selectorWrapper);

  // Divider
  const divider = document.createElement('div');
  divider.className = 'customer-required-overlay__divider';
  divider.innerHTML = '<span>oder</span>';
  card.appendChild(divider);

  // Button to navigate to customers page
  const manageBtn = document.createElement('button');
  manageBtn.type = 'button';
  manageBtn.className = 'btn btn-secondary customer-required-overlay__manage-btn';
  manageBtn.innerHTML = `${addCustomerIcon} <span>Neuen Kunden anlegen</span>`;
  manageBtn.addEventListener('click', () => {
    hideCustomerRequiredOverlay();
    navigate('/customers');
  });
  card.appendChild(manageBtn);

  overlayElement.appendChild(card);
  contentContainer.appendChild(overlayElement);

  // Listen for customer selection changes (e.g. from sidebar)
  unsubscribeUser = userService.onUserChanged(() => {
    const customerNumber = userService.getCustomerNumber();
    if (customerNumber && customerNumber.trim()) {
      hideCustomerRequiredOverlay();
      // Force re-render of the current route to load the page
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    }
  });
}

/**
 * Hide and remove the customer-required overlay.
 */
export function hideCustomerRequiredOverlay(): void {
  if (selectorHandle) {
    selectorHandle.destroy();
    selectorHandle = null;
  }
  if (unsubscribeUser) {
    unsubscribeUser();
    unsubscribeUser = null;
  }
  if (overlayElement) {
    overlayElement.remove();
    overlayElement = null;
  }
}

/**
 * Returns true if the overlay is currently visible.
 */
export function isCustomerOverlayVisible(): boolean {
  return overlayElement !== null;
}
